
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function CursoPage() {
  const niveles = [
    {
      nivel: 'PRINCIPIANTE',
      color: 'green',
      modulos: 6,
      descripcion: 'Introducción a conceptos básicos de IA y tecnología',
      temas: ['¿Qué es la IA?', 'Historia de la tecnología', 'Dispositivos inteligentes', 'Primeros pasos']
    },
    {
      nivel: 'NOVATO',
      color: 'blue', 
      modulos: 5,
      descripcion: 'Fundamentos de algoritmos y programación básica',
      temas: ['Algoritmos simples', 'Lógica de programación', 'Patrones', 'Resolución de problemas']
    },
    {
      nivel: 'INTERMEDIO',
      color: 'purple',
      modulos: 5,
      descripcion: 'Machine Learning básico y aplicaciones prácticas',
      temas: ['Aprendizaje automático', 'Datos y patrones', 'Predicciones', 'Herramientas IA']
    },
    {
      nivel: 'AVANZADO',
      color: 'red',
      modulos: 4,
      descripcion: 'Deep Learning y proyectos complejos',
      temas: ['Redes neuronales', 'Visión artificial', 'Procesamiento de lenguaje', 'Proyectos avanzados']
    },
    {
      nivel: 'EXPERTO',
      color: 'yellow',
      modulos: 4,
      descripcion: 'Especialización y aplicaciones profesionales',
      temas: ['IA especializada', 'Ética en IA', 'Proyectos finales', 'Certificación']
    }
  ];

  useEffect(() => {
    // Schema.org JSON-LD for Course Details
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Curso de Inteligencia Artificial - 24 Módulos",
      "description": "Curso completo de Inteligencia Artificial diseñado para instituciones educativas, cubriendo desde conceptos básicos hasta aplicaciones avanzadas",
      "provider": {
        "@type": "Organization",
        "name": "INDEFINHUM",
        "url": import.meta.env.VITE_SITE_URL
      },
      "courseCode": "IA-EDU-24",
      "numberOfCredits": 24,
      "educationalLevel": ["Elementary School", "Middle School", "High School"],
      "timeRequired": "P1Y",
      "coursePrerequisites": "Ninguno - Curso diseñado para todos los niveles",
      "teaches": [
        "Fundamentos de Inteligencia Artificial",
        "Algoritmos y Patrones",
        "Machine Learning Básico",
        "Redes Neuronales Artificiales",
        "Visión por Computadora",
        "Procesamiento de Lenguaje Natural",
        "IA Ética y Responsable",
        "Desarrollo de Aplicaciones con IA"
      ],
      "syllabusSections": [
        {
          "@type": "Syllabus",
          "name": "Nivel Principiante",
          "description": "Módulos 1-5: Introducción básica a conceptos fundamentales de IA"
        },
        {
          "@type": "Syllabus", 
          "name": "Nivel Novato",
          "description": "Módulos 6-10: Conceptos intermedios y primeras aplicaciones prácticas"
        },
        {
          "@type": "Syllabus",
          "name": "Nivel Intermedio", 
          "description": "Módulos 11-15: Desarrollo de habilidades técnicas y proyectos"
        },
        {
          "@type": "Syllabus",
          "name": "Nivel Avanzado",
          "description": "Módulos 16-20: Especialización en áreas específicas de IA"
        },
        {
          "@type": "Syllabus",
          "name": "Nivel Experto",
          "description": "Módulos 21-24: Investigación avanzada y proyectos profesionales"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.4), rgba(59, 130, 246, 0.4)), url('https://public.readdy.ai/ai/img_res/408b891aee81da050d9bfa9baf2147e3.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Curso de <span className="text-cyan-300">Inteligencia Artificial</span>
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
            Un programa educativo revolucionario diseñado para preparar a los estudiantes del siglo XXI 
            con las habilidades más demandadas del futuro digital
          </p>
        </div>
      </section>

      {/* Importancia de la IA */}
      <section className="py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              La Revolución de la <span className="text-cyan-300">Inteligencia Artificial</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto mb-12">
              Estamos viviendo la transformación tecnológica más significativa de la historia. 
              La IA no es solo una herramienta del futuro, es la realidad del presente que está 
              redefiniendo cada aspecto de nuestras vidas.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">Impacto Transformador en la Educación</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Personalización del Aprendizaje</h4>
                    <p className="text-blue-100">La IA permite adaptar el contenido educativo al ritmo y estilo de aprendizaje de cada estudiante.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-cyan-5

0 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Desarrollo de Habilidades del Siglo XXI</h4>
                    <p className="text-blue-100">Pensamiento crítico, resolución de problemas complejos y creatividad digital.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Preparación para Carreras Emergentes</h4>
                    <p className="text-blue-100">85% de los trabajos de 2030 aún no existen, y la mayoría requerirán conocimientos de IA.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Alfabetización Digital Avanzada</h4>
                    <p className="text-blue-100">Comprensión profunda de cómo funcionan las tecnologías que definirán el futuro.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <img 
                src="https://readdy.ai/api/search-image?query=Children%20using%20advanced%20AI%20technology%20in%20classroom%2C%20bright%20modern%20educational%20environment%2C%20diverse%20students%20engaged%20with%20artificial%20intelligence%20tools%2C%20futuristic%20learning%20with%20tablets%20and%20AI%20interfaces%2C%20professional%20educational%20photography&width=600&height=400&seq=ai-students&orientation=landscape"
                alt="Estudiantes usando IA en el aula"
                className="rounded-2xl shadow-xl w-full"
              />
              
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20">
                <h4 className="text-xl font-bold text-white mb-4 text-center">Estadísticas de Impacto</h4>
                <div className="grid grid-cols-2 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-cyan-400 mb-2">300%</div>
                    <div className="text-sm text-blue-200">Crecimiento en empleos de IA</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-400 mb-2">$15.7T</div>
                    <div className="text-sm text-blue-200">Contribución global de IA en 2030</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-400 mb-2">75%</div>
                    <div className="text-sm text-blue-200">Empresas adoptarán IA</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-yellow-400 mb-2">40%</div>
                    <div className="text-sm text-blue-200">Mejora en productividad</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparación Visual */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-white/20">
            <h3 className="text-3xl font-bold text-center text-white mb-12">
              La Diferencia es <span className="text-cyan-300">Transformadora</span>
            </h3>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div className="text-center">
                <div className="mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Students%20struggling%20with%20traditional%20education%20methods%2C%20outdated%20classroom%20environment%2C%20children%20looking%20confused%20with%20old%20textbooks%2C%20traditional%20teaching%20without%20technology%2C%20educational%20challenges%20without%20AI&width=400&height=300&seq=traditional-education&orientation=landscape"
                    alt="Educación tradicional sin IA"
                    className="rounded-xl shadow-lg mx-auto mb-4 w-full max-w-sm"
                  />
                </div>
                <h4 className="text-xl font-semibold text-red-300 mb-4">Sin Educación en IA</h4>
                <ul className="text-left space-y-2 text-blue-200">
                  <li className="flex items-center"><i className="ri-close-circle-line text-red-400 mr-2"></i>Limitaciones en resolución de problemas</li>
                  <li className="flex items-center"><i className="ri-close-circle-line text-red-400 mr-2"></i>Menor competitividad académica</li>
                  <li className="flex items-center"><i className="ri-close-circle-line text-red-400 mr-2"></i>Brecha digital significativa</li>
                  <li className="flex items-center"><i className="ri-close-circle-line text-red-400 mr-2"></i>Desventaja en oportunidades futuras</li>
                </ul>
              </div>

              <div className="text-center">
                <div className="mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Students%20excelling%20with%20AI%20education%2C%20confident%20children%20using%20advanced%20technology%2C%20modern%20classroom%20with%20AI%20tools%2C%20diverse%20students%20engaged%20with%20artificial%20intelligence%2C%20bright%20successful%20learning%20environment&width=400&height=300&seq=ai-education-success&orientation=landscape"
                    alt="Educación con IA"
                    className="rounded-xl shadow-lg mx-auto mb-4 w-full max-w-sm"
                  />
                </div>
                <h4 className="text-xl font-semibold text-green-300 mb-4">Con Educación en IA</h4>
                <ul className="text-left space-y-2 text-blue-200">
                  <li className="flex items-center"><i className="ri-check-circle-line text-green-400 mr-2"></i>Pensamiento crítico desarrollado</li>
                  <li className="flex items-center"><i className="ri-check-circle-line text-green-400 mr-2"></i>Ventaja competitiva significativa</li>
                  <li className="flex items-center"><i className="ri-check-circle-line text-green-400 mr-2"></i>Preparación para el futuro digital</li>
                  <li className="flex items-center"><i className="ri-check-circle-line text-green-400 mr-2"></i>Múltiples oportunidades profesionales</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estructura del Curso */}
      <section className="py-20 bg-gradient-to-r from-purple-600/20 to-cyan-500/20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Estructura del <span className="text-cyan-300">Programa</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">
              24 módulos cuidadosamente diseñados en 5 niveles progresivos, adaptados a diferentes 
              edades y niveles de conocimiento para garantizar un aprendizaje efectivo y duradero.
            </p>
          </div>

          <div className="grid gap-8">
            {niveles.map((nivel, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 hover:shadow-lg transition-shadow border border-white/20">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="lg:w-1/3">
                    <img 
                      src={`https://readdy.ai/api/search-image?query=AI%20education%20level%20$%7Bnivel.nivel.toLowerCase%28%29%7D%2C%20modern%20technology%20learning%2C%20artificial%20intelligence%20concepts%20for%20$%7Bnivel.nivel.toLowerCase%28%29%7D%20students%2C%20educational%20technology%20interface%2C%20bright%20professional%20educational%20environment&width=400&height=300&seq=${nivel.nivel.toLowerCase()}-level&orientation=landscape`}
                      alt={`Nivel ${nivel.nivel}`}
                      className="rounded-2xl shadow-lg w-full"
                    />
                  </div>
                  
                  <div className="lg:w-2/3">
                    <div className="flex items-center mb-4">
                      <span className={`bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold mr-4`}>
                        NIVEL {index + 1}
                      </span>
                      <span className="text-blue-200">{nivel.modulos} Módulos</span>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-white mb-4">{nivel.nivel}</h3>
                    <p className="text-lg text-blue-100 mb-6">{nivel.descripcion}</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {nivel.temas.map((tema, temaIndex) => (
                        <div key={temaIndex} className="flex items-center bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm rounded-lg p-3 shadow-sm border border-white/20">
                          <i className={`ri-check-line text-cyan-400 mr-3`}></i>
                          <span className="text-white">{tema}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link 
              to="/modulos"
              className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-cyan-500 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap inline-flex items-center"
            >
              <i className="ri-list-check mr-2"></i>
              Ver Todos los Módulos
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
